import { useEffect, useMemo } from 'react'
import { Typography, useTheme } from '@mui/material'
import { AccountReactivateOutlineIcon } from '@weblite/icons'
import { useHistoryCustomBack } from 'weblite-router'
import { SecondaryButton } from '$common/buttons'
import { Tour, TourOptions, TourStep } from '$common/tour'
import { showHowToSyncIOSContactsModal } from '$comp/modals/how-to-sync-ios-contacts/how-to-sync-ios-contacts.api'
import { useLastCallback } from '$hooks/use-last-callback'
import { selectContacts } from '$logic/contacts/service'
import { isContactsSupported } from '$func/environmentInfo'
import { t } from '$i18n'
import { useOnboarding } from '$logic/onboarding/react-hooks'
import { IosSafariOnboarding } from '$logic/onboarding/types'

const CONTAINER_EL_ID = 'sync-contacts-fab'

export function SyncIOSContactsFab() {
  const theme = useTheme()

  const {
    enabled: tourEnabled,
    completeOnboarding: completeTour,
    run: runTour,
    shouldRunOnboarding: shouldRunTour,
  } = useOnboarding(IosSafariOnboarding.IOS_SYNC_CONTACTS, false)

  const handleClick = useLastCallback(() => {
    if (isContactsSupported()) void selectContacts()
    else void showHowToSyncIOSContactsModal()
  })

  useHistoryCustomBack(completeTour, [tourEnabled, completeTour], tourEnabled)

  useEffect(() => {
    let timeout: NodeJS.Timeout

    if (shouldRunTour)
      timeout = setTimeout(() => {
        runTour()
      }, 2000)

    return () => {
      if (timeout) clearTimeout(timeout)
    }
  }, [shouldRunTour])

  const tourSteps = useMemo<TourStep[]>(
    () => [
      {
        target: `#${CONTAINER_EL_ID}`,
        content: t('syncIOSAccounts.onboarding.title'),
        disableBeacon: true,
      },
    ],
    [],
  )

  const tourOptions: TourOptions = useMemo(
    () => ({
      doneLabel: t('common.understand'),
    }),
    [],
  )

  return (
    <>
      <Tour
        steps={tourSteps}
        enabled={tourEnabled}
        options={tourOptions}
        overlayStyles={{
          background: theme.palette.backdrop,
        }}
        spotLightPadding={8}
        hideSkipButton
        onComplete={completeTour}
        onSkip={completeTour}
      />
      <SecondaryButton
        id={CONTAINER_EL_ID}
        sx={{
          borderRadius: '24px',
          padding: '12px',
        }}
        text={
          <Typography variant="subtitle3" color="neutral900">
            {t('syncIOSAccounts.fabButtonLabel')}
          </Typography>
        }
        onClick={handleClick}
        size="medium"
        endIcon={
          <AccountReactivateOutlineIcon
            sx={{ width: '24px', height: '24px', mr: '4px' }}
          />
        }
      />
    </>
  )
}
